import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Sparkline } from "../../components/Sparkline";
import { getDevStats, getMyApps } from "../../api/endpoints";
import type { StatsByDayPoint } from "../../api/types";
import { ApiError } from "../../api/types";

export default function StatsPage() {
  const [params, setParams] = useSearchParams();
  const pkgFromUrl = params.get("pkg") ?? "";

  // Если pkg не задан — берём первое приложение разработчика и подставляем в URL.
  const myAppsQ = useQuery({
    queryKey: ["dev", "my-apps"],
    queryFn: getMyApps,
    enabled: !pkgFromUrl,
  });
  const pkg = pkgFromUrl || myAppsQ.data?.[0]?.pkg || "";

  const statsQ = useQuery({
    queryKey: ["dev", "stats", pkg],
    queryFn: () => getDevStats(pkg),
    enabled: !!pkg,
    retry: (count, err) => {
      if (err instanceof ApiError && (err.status === 403 || err.status === 404)) return false;
      return count < 1;
    },
  });

  const data = statsQ.data;
  const loading = statsQ.isLoading || (!pkg && myAppsQ.isLoading);
  const apps = myAppsQ.data ?? (data ? [{ pkg: data.pkg, name: data.pkg }] : []);

  return (
    <>
      <header className="flex flex-col gap-3 pb-5 sm:flex-row sm:items-end sm:justify-between sm:gap-4">
        <div className="min-w-0">
          <h1 className="text-[20px] font-semibold leading-tight tracking-[-0.01em] text-[var(--color-fg)] sm:text-[22px]">
            Статистика{pkg && <> · <span className="font-mono">{pkg}</span></>}
          </h1>
          <div className="mt-1 text-[12.5px] text-[var(--color-fg-muted)]">
            {data ? `${data.window_days} дней · события install.success / install.error` : "Выберите приложение"}
          </div>
        </div>
        <div className="flex gap-2">
          <select
            value={pkg}
            onChange={(e) => setParams({ pkg: e.target.value })}
            className="rounded-lg border border-[var(--color-border-strong)] bg-[var(--color-surface)] px-3 py-1.5 text-[12px] font-medium text-[var(--color-fg)] outline-none hover:bg-[var(--color-surface-2)]"
          >
            {!pkg && <option value="">— приложение —</option>}
            {apps.map((a) => (
              <option key={a.pkg} value={a.pkg}>{a.pkg}</option>
            ))}
          </select>
        </div>
      </header>

      {statsQ.isError && (
        <div className="mb-3 rounded-xl border border-[var(--color-danger)]/40 bg-[var(--color-danger-bg)] px-4 py-3 text-[12.5px] text-[var(--color-danger)]">
          {statsQ.error instanceof ApiError
            ? statsQ.error.message
            : "Не удалось загрузить статистику"}
        </div>
      )}

      <div className={"grid grid-cols-2 gap-3 xl:grid-cols-4 " + (loading ? "opacity-70" : "")}>
        <Metric
          label="Установки"
          value={data ? data.total_installs.toLocaleString("ru-RU") : "—"}
          trend={data ? `за ${data.window_days} дн.` : "—"}
          color="var(--color-brand-2)"
        />
        <Metric
          label="Ошибки установки"
          value={data ? data.total_errors.toLocaleString("ru-RU") : "—"}
          trend={data ? `за ${data.window_days} дн.` : "—"}
          color={data && data.total_errors > 0 ? "var(--color-warning)" : "var(--color-fg-muted)"}
        />
        <Metric
          label="Success rate"
          value={data ? `${(data.success_rate * 100).toFixed(2)} %` : "—"}
          trend={data ? `success / (success + error)` : "—"}
          color={data && data.success_rate >= 0.99 ? "var(--color-success)" : "var(--color-warning)"}
        />
        <Metric
          label="Окно"
          value={data ? `${data.window_days} дн.` : "—"}
          trend="скользящее"
          color="var(--color-fg-muted)"
        />
      </div>

      <div className="mt-3 grid grid-cols-1 gap-3">
        <Histogram points={data?.by_day ?? []} />
      </div>
    </>
  );
}

function Metric({
  label,
  value,
  trend,
  color,
}: {
  label: string;
  value: string;
  trend: string;
  color: string;
}) {
  // Локальный шум для маленького Sparkline — это вспомогательная вьюшка,
  // основное — крупная цифра.
  const values = useMemo(
    () => Array.from({ length: 30 }, (_, i) => 100 + Math.round(Math.sin(i / 3) * 25) + i * 3),
    [],
  );
  return (
    <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
      <div className="text-[10.5px] font-medium uppercase tracking-[0.08em] text-[var(--color-fg-muted)]">{label}</div>
      <div className="mt-2 text-[26px] font-semibold leading-none tracking-[-0.02em] text-[var(--color-fg)]">{value}</div>
      <div className="mt-2 text-[11.5px] text-[var(--color-fg-muted)]">{trend}</div>
      <Sparkline values={values} color={color} className="mt-3 h-8 w-full" />
    </div>
  );
}

function Histogram({ points }: { points: StatsByDayPoint[] }) {
  const installs = points.map((p) => p.installs);
  const errors = points.map((p) => p.errors);
  const max = Math.max(1, ...installs, ...errors);

  // Подписи: первое, /4, /2, 3/4, последнее (по короткой дате dd.mm)
  const ticks = useMemo(() => {
    if (!points.length) return [] as { idx: number; label: string }[];
    const i0 = 0;
    const i1 = Math.floor(points.length / 4);
    const i2 = Math.floor(points.length / 2);
    const i3 = Math.floor((3 * points.length) / 4);
    const i4 = points.length - 1;
    const fmt = (s: string) => {
      const [, m, d] = s.split("-");
      return `${d}.${m}`;
    };
    return [i0, i1, i2, i3, i4].map((idx) => ({ idx, label: fmt(points[idx].date) }));
  }, [points]);

  return (
    <section className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
      <div className="flex items-baseline justify-between">
        <h2 className="text-[14px] font-semibold text-[var(--color-fg)]">Установки по дням</h2>
        <div className="flex items-center gap-3 text-[11px] text-[var(--color-fg-muted)]">
          <Legend color="var(--color-brand)" label="успех" />
          <Legend color="var(--color-danger)" label="ошибка" />
        </div>
      </div>

      {!points.length ? (
        <div className="mt-6 grid h-40 place-items-center rounded-lg border border-dashed border-[var(--color-border-strong)] text-[12.5px] text-[var(--color-fg-muted)]">
          Нет данных
        </div>
      ) : (
        <>
          <div className="mt-4 flex h-40 items-end gap-1">
            {points.map((p, i) => {
              const hOk = (p.installs / max) * 100;
              const hErr = (p.errors / max) * 100;
              return (
                <div
                  key={p.date}
                  className="flex flex-1 flex-col items-center justify-end gap-0.5"
                  title={`${p.date} · установок ${p.installs} · ошибок ${p.errors}`}
                >
                  {p.errors > 0 && (
                    <div
                      className="w-full rounded-t-[2px]"
                      style={{
                        height: `${hErr}%`,
                        background: "var(--color-danger)",
                        animation: `bar-grow 600ms cubic-bezier(0.22, 1, 0.36, 1) ${i * 8}ms both`,
                      }}
                    />
                  )}
                  <div
                    className="w-full rounded-t-[3px]"
                    style={{
                      height: `${hOk}%`,
                      background: "var(--color-brand)",
                      animation: `bar-grow 600ms cubic-bezier(0.22, 1, 0.36, 1) ${i * 8}ms both`,
                    }}
                  />
                </div>
              );
            })}
          </div>
          <div className="mt-2 flex justify-between text-[10px] text-[var(--color-fg-muted)]">
            {ticks.map((t) => (
              <span key={t.idx}>{t.label}</span>
            ))}
          </div>
          <style>{`@keyframes bar-grow { from { transform: scaleY(0); transform-origin: bottom } to { transform: scaleY(1); } }`}</style>
        </>
      )}
    </section>
  );
}

function Legend({ color, label }: { color: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="inline-block h-2 w-2 rounded-full" style={{ background: color }} />
      {label}
    </span>
  );
}
